import { AppContainer } from "@/components/layout/app-container"
import { Button } from "@/components/ui/button"
import { ArrowLeft, AlertTriangle, WifiOff, FileQuestion } from "lucide-react"
import { useLocation } from "wouter"
import { AxiosError } from "axios"
import { PageErrorProps } from "../contrato.types"

function getErrorInfo(error: unknown, entityName: string, entityId?: string | number) {
  const axiosError = error as AxiosError

  if (axiosError?.response?.status === 404) {
    return {
      icon: <FileQuestion className="w-12 h-12 text-gray-400 mx-auto mb-4" />,
      message: entityId
        ? `${entityName} com ID ${entityId} não encontrado`
        : `${entityName} não encontrado`,
      description: "O recurso solicitado não existe ou foi removido."
    }
  }

  if (axiosError?.message === "Network Error") {
    return {
      icon: <WifiOff className="w-12 h-12 text-yellow-500 mx-auto mb-4" />,
      message: "Erro de conexão",
      description: "Backend não está acessível. Verifique se o servidor está rodando."
    }
  }

  return {
    icon: <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />,
    message: "Erro ao comunicar com o servidor",
    description: axiosError?.message || "Ocorreu um erro inesperado. Tente novamente."
  }
}

export function PageError({
  title = "Erro",
  error,
  entityName = "Recurso",
  entityId,
  backUrl = "/",
  backLabel = "Voltar",
  onRetry,
  showBackButton = true,
  children
}: PageErrorProps) {
  const [, navigate] = useLocation()

  const errorInfo = getErrorInfo(error, entityName, entityId)

  return (
    <AppContainer title={title} description="">
      {showBackButton && (
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(backUrl)}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
        </div>
      )}

      <div className="text-center py-12 bg-[#15203c] rounded-[20px]">
        {errorInfo.icon}
        <p className="text-white font-medium mb-2">{errorInfo.message}</p>
        <p className="text-gray-400 text-sm mb-6">{errorInfo.description}</p>

        {children}

        <div className="flex gap-3 justify-center">
          {onRetry && (
            <Button variant="outline" onClick={onRetry}>
              Tentar novamente
            </Button>
          )}
          <Button onClick={() => navigate(backUrl)}>
            {backLabel}
          </Button>
        </div>
      </div>
    </AppContainer>
  )
}
